import {MAX_CONCURRENT_REQUESTS} from '../constants';

type QueueTask<T> = {
    run: () => Promise<T>;
    resolve: (value: T) => void;
    reject: (reason: unknown) => void;
    cancelled: boolean;
};

/**
 * Caps in-flight async work at MAX_CONCURRENT_REQUESTS (or a custom limit).
 * Distinct from ConcurrentRequests, which only dedupes identical in-flight calls.
 */
export class ConcurrentRequestQueue {
    private readonly maxConcurrent: number;
    private active = 0;
    private readonly pending: QueueTask<unknown>[] = [];

    constructor(maxConcurrent: number = MAX_CONCURRENT_REQUESTS) {
        this.maxConcurrent = Math.max(1, maxConcurrent);
    }

    /** Schedules work; returns a handle so callers can drop a queued (not yet started) load. */
    enqueue<T>(run: () => Promise<T>): {promise: Promise<T>; cancel: () => void} {
        let task: QueueTask<T>;
        const promise = new Promise<T>((resolve, reject) => {
            task = {run, resolve, reject, cancelled: false};
            this.pending.push(task as QueueTask<unknown>);
            this.pump();
        });
        return {
            promise,
            cancel: () => {
                task.cancelled = true;
            },
        };
    }

    private pump(): void {
        while (this.active < this.maxConcurrent && this.pending.length) {
            const task = this.pending.shift()!;
            if (task.cancelled) {
                continue;
            }
            this.active++;
            task.run()
                .then(value => {
                    if (!task.cancelled) {
                        task.resolve(value);
                    }
                })
                .catch(reason => {
                    if (!task.cancelled) {
                        task.reject(reason);
                    }
                })
                .finally(() => {
                    this.active--;
                    this.pump();
                });
        }
    }
}
